import React from 'react';

/**
 * Lightweight markdown renderer for agent messages in the chat widget.
 *
 * Supports: headings (## / ###), **bold**, *italic*, `inline code`,
 * ```code blocks```, [links](url), - unordered lists, 1. ordered lists,
 * > blockquotes, --- horizontal rules, and line breaks.
 *
 * No external dependencies — keeps the widget bundle small.
 */

interface MarkdownTextProps {
  content: string;
  className?: string;
}

interface TextNode {
  type: 'text' | 'bold' | 'italic' | 'code' | 'link';
  content: string;
  href?: string;
}

function isSafeUrl(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.href);
    return ['https:', 'http:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

function parseInline(text: string): TextNode[] {
  const nodes: TextNode[] = [];
  // Matches: `code`, **bold**, *italic*, [md link](url),
  // <a href="url">label</a>, <strong>text</strong>, <em>text</em>, <b>text</b>, <i>text</i>, <br>, <br/>
  const pattern = /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[[^\]]+\]\([^)]+\))|(<a\s+href="([^"]+)"[^>]*>([^<]+)<\/a>)|(<strong>([^<]+)<\/strong>)|(<b>([^<]+)<\/b>)|(<em>([^<]+)<\/em>)|(<i>([^<]+)<\/i>)|(<br\s*\/?>)/gi;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push({ type: 'text', content: text.slice(lastIndex, match.index) });
    }

    if (match[1]) {
      // `inline code`
      nodes.push({ type: 'code', content: match[1].slice(1, -1) });
    } else if (match[2]) {
      // **bold**
      nodes.push({ type: 'bold', content: match[2].slice(2, -2) });
    } else if (match[3]) {
      // *italic*
      nodes.push({ type: 'italic', content: match[3].slice(1, -1) });
    } else if (match[4]) {
      // [label](url)
      const linkMatch = match[4].match(/\[([^\]]+)\]\(([^)]+)\)/);
      if (linkMatch) {
        nodes.push({ type: 'link', content: linkMatch[1], href: linkMatch[2] });
      }
    } else if (match[5]) {
      // <a href="url">label</a>
      nodes.push({ type: 'link', content: match[7], href: match[6] });
    } else if (match[8]) {
      // <strong>text</strong>
      nodes.push({ type: 'bold', content: match[9] });
    } else if (match[10]) {
      // <b>text</b>
      nodes.push({ type: 'bold', content: match[11] });
    } else if (match[12]) {
      // <em>text</em>
      nodes.push({ type: 'italic', content: match[13] });
    } else if (match[14]) {
      // <i>text</i>
      nodes.push({ type: 'italic', content: match[15] });
    } else if (match[16]) {
      // <br> or <br/>
      nodes.push({ type: 'text', content: '\n' });
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push({ type: 'text', content: text.slice(lastIndex) });
  }

  return nodes;
}

function renderInline(nodes: TextNode[]): React.ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.type) {
      case 'bold':
        return <strong key={i} className="font-semibold">{node.content}</strong>;
      case 'italic':
        return <em key={i}>{node.content}</em>;
      case 'code':
        return (
          <code key={i} className="px-1 py-0.5 rounded bg-black/10 text-[0.85em] font-mono">
            {node.content}
          </code>
        );
      case 'link':
        return node.href && isSafeUrl(node.href) ? (
          <a
            key={i}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-1 underline-offset-2 hover:opacity-80"
          >
            {node.content}
          </a>
        ) : (
          <span key={i}>{node.content}</span>
        );
      default:
        return <span key={i}>{node.content}</span>;
    }
  });
}

export const MarkdownText: React.FC<MarkdownTextProps> = ({ content, className }) => {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let listItems: string[] = [];
  let listType: 'ul' | 'ol' | null = null;
  let codeBlock: string[] | null = null;
  let blockquoteLines: string[] = [];

  const flushList = () => {
    if (listItems.length === 0) return;
    const Tag = listType === 'ol' ? 'ol' : 'ul';
    const listClass = listType === 'ol'
      ? 'list-decimal pl-5 space-y-0.5 marker:text-current/50'
      : 'list-disc pl-5 space-y-0.5 marker:text-current/50';
    elements.push(
      <Tag key={elements.length} className={listClass}>
        {listItems.map((item, j) => (
          <li key={j} className="pl-0.5">{renderInline(parseInline(item))}</li>
        ))}
      </Tag>
    );
    listItems = [];
    listType = null;
  };

  const flushBlockquote = () => {
    if (blockquoteLines.length === 0) return;
    elements.push(
      <blockquote
        key={elements.length}
        className="border-l-[3px] border-current/20 pl-3 italic opacity-80"
      >
        {blockquoteLines.map((bqLine, j) => (
          <p key={j} className="break-words">{renderInline(parseInline(bqLine))}</p>
        ))}
      </blockquote>
    );
    blockquoteLines = [];
  };

  for (const line of lines) {
    // Code block toggle
    if (line.trim().startsWith('```')) {
      if (codeBlock === null) {
        flushList();
        flushBlockquote();
        codeBlock = [];
      } else {
        elements.push(
          <pre
            key={elements.length}
            className="rounded-lg bg-gray-900 text-gray-100 text-xs leading-relaxed p-3 overflow-x-auto font-mono"
          >
            <code>{codeBlock.join('\n')}</code>
          </pre>
        );
        codeBlock = null;
      }
      continue;
    }

    if (codeBlock !== null) {
      codeBlock.push(line);
      continue;
    }

    // Headings
    const h2Match = line.match(/^##\s+(.+)/);
    const h3Match = line.match(/^###\s+(.+)/);
    if (h2Match) {
      flushList();
      flushBlockquote();
      elements.push(
        <h3 key={elements.length} className="font-bold text-[0.95em] mt-2 mb-0.5">
          {renderInline(parseInline(h2Match[1]))}
        </h3>
      );
      continue;
    }
    if (h3Match) {
      flushList();
      flushBlockquote();
      elements.push(
        <h4 key={elements.length} className="font-semibold text-[0.9em] mt-1.5 mb-0.5">
          {renderInline(parseInline(h3Match[1]))}
        </h4>
      );
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line.trim())) {
      flushList();
      flushBlockquote();
      elements.push(<hr key={elements.length} className="border-current/15 my-2" />);
      continue;
    }

    // Blockquote
    const bqMatch = line.match(/^>\s?(.*)/);
    if (bqMatch) {
      flushList();
      blockquoteLines.push(bqMatch[1]);
      continue;
    } else if (blockquoteLines.length > 0) {
      flushBlockquote();
    }

    // Unordered list
    const ulMatch = line.match(/^[\-\*]\s+(.+)/);
    const olMatch = line.match(/^\d+\.\s+(.+)/);

    if (ulMatch) {
      flushBlockquote();
      if (listType === 'ol') flushList();
      listType = 'ul';
      listItems.push(ulMatch[1]);
    } else if (olMatch) {
      flushBlockquote();
      if (listType === 'ul') flushList();
      listType = 'ol';
      listItems.push(olMatch[1]);
    } else {
      flushList();
      if (line.trim() === '') {
        // Empty line — add spacing but don't stack multiple <br>s
        if (elements.length > 0) {
          const last = elements[elements.length - 1];
          if (last && (last as React.ReactElement).type !== 'br') {
            elements.push(<div key={elements.length} className="h-1" />);
          }
        }
      } else {
        elements.push(
          <p key={elements.length} className="break-words leading-relaxed">
            {renderInline(parseInline(line))}
          </p>
        );
      }
    }
  }

  // Flush remaining
  flushList();
  flushBlockquote();
  if (codeBlock !== null) {
    // Unclosed code block — render as-is
    elements.push(
      <pre
        key={elements.length}
        className="rounded-lg bg-gray-900 text-gray-100 text-xs leading-relaxed p-3 overflow-x-auto font-mono"
      >
        <code>{codeBlock.join('\n')}</code>
      </pre>
    );
  }

  return <div className={className}>{elements}</div>;
};
